import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  LinearProgress,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import SignupApi, { TrialSignupRequest } from "api/SignupApi";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

type Step = 1 | 2 | 3 | "done";

type FormState = {
  // Step 1
  companyName: string;
  kana: string;
  representativeName: string;
  email: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  // Step 2
  businessType: "delivery" | "visit";
  openingTime: string; // "HH:mm" 開店時間
  closingTime: string; // "HH:mm" 閉店時間 (日付変更時間は閉店+3hで自動算出)
  pointShopFlag: boolean; // false=一括 true=店舗別
  guestCategoryShopFlag: boolean;
  examEnabled: boolean;
  examType: "orderCount" | "shiftCount";
  examCount: number;
  // Step 3
  referralSource: string;
  agreedTerms: boolean;
};

const initialState: FormState = {
  companyName: "",
  kana: "",
  representativeName: "",
  email: "",
  phone: "",
  password: "",
  passwordConfirm: "",
  businessType: "delivery",
  openingTime: "10:00",
  closingTime: "05:00",
  pointShopFlag: false,
  guestCategoryShopFlag: false,
  examEnabled: false,
  examType: "orderCount",
  examCount: 50,
  referralSource: "",
  agreedTerms: false,
};

const Signup = () => {
  const { t } = useTranslation("signup");
  const REFERRAL_OPTIONS = [
    t("referralOptions.google"),
    t("referralOptions.referral"),
    t("referralOptions.sns"),
    t("referralOptions.salesMail"),
    t("referralOptions.other"),
  ];
  const history = useHistory();
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormState>(initialState);
  const [verifyToken, setVerifyToken] = useState<string>("");
  const [smsCode, setSmsCode] = useState<string>("");
  const [smsSent, setSmsSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
  };

  // ---- Step 1 validation & next ----
  const validateStep1 = (): string | null => {
    if (!form.companyName) return t("errors.companyNameRequired");
    if (!form.kana) return t("errors.kanaRequired");
    if (!form.representativeName) return t("errors.representativeRequired");
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      return t("errors.emailInvalid");
    if (!form.phone.match(/^[0-9+\-\s]{10,16}$/))
      return t("errors.phoneInvalid");
    if (form.password.length < 8) return t("errors.passwordTooShort");
    if (form.password !== form.passwordConfirm)
      return t("errors.passwordMismatch");
    return null;
  };

  const goStep2 = () => {
    const err = validateStep1();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setStep(2);
  };

  // ---- Step 2 validation & next ----
  const goStep3 = () => {
    // Step 2 has no required fields that can be invalid
    setError("");
    setStep(3);
  };

  // ---- SMS Send ----
  const sendSmsCode = async () => {
    setLoading(true);
    setError("");
    try {
      // 本番では reCAPTCHA token をここで取得
      const recaptcha = "dev-skip";
      await SignupApi.sendSmsCode(form.phone, recaptcha);
      setSmsSent(true);
    } catch (e: any) {
      setError(e?.message || t("errors.smsSendFailed"));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (smsCode.length !== 6) {
      setError(t("errors.codeLengthInvalid"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await SignupApi.verifySmsCode(form.phone, smsCode);
      setVerifyToken(res.token);
    } catch (e: any) {
      setError(e?.message || t("errors.codeMismatch"));
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!form.agreedTerms) {
      setError(t("errors.termsRequired"));
      return;
    }
    if (!verifyToken) {
      setError(t("errors.smsAuthRequired"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload: TrialSignupRequest = {
        companyName: form.companyName,
        kana: form.kana,
        representativeName: form.representativeName,
        email: form.email,
        phone: form.phone,
        password: form.password,
        businessType: form.businessType,
        openingTime: `${form.openingTime}:00`,
        closingTime: `${form.closingTime}:00`,
        pointShopFlag: form.pointShopFlag,
        guestCategoryShopFlag: form.guestCategoryShopFlag,
        castExaminationType: form.examEnabled ? form.examType : null,
        castExaminationCount: form.examEnabled ? form.examCount : null,
        referralSource: form.referralSource || null,
        verifyToken,
        agreedTerms: form.agreedTerms,
      };
      await SignupApi.createTrialAccount(payload);
      setStep("done");
    } catch (e: any) {
      setError(e?.message || t("errors.createFailed"));
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = step === 1 ? 33 : step === 2 ? 66 : 100;

  return (
    <Box
      display="flex"
      justifyContent="center"
      minHeight="calc(100vh - 200px)"
      bgcolor="#f5f7fa"
      py={6}
    >
      <Paper style={{ width: "100%", maxWidth: 720, padding: 32 }}>
        <Typography variant="h4" style={{ fontWeight: "bold", marginBottom: 8 }}>
          {t("title")}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t("description")}
        </Typography>

        {step !== "done" && (
          <Box mt={3}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="caption" color="textSecondary">
                {t("stepIndicator", {
                  current: step === 1 ? "1" : step === 2 ? "2" : "3",
                  total: "3",
                })}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {step === 1
                  ? t("step1Label")
                  : step === 2
                  ? t("step2Label")
                  : t("step3Label")}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressPercent} />
          </Box>
        )}

        {error && (
          <Alert severity="error" style={{ marginTop: 16 }}>
            {error}
          </Alert>
        )}

        {step === 1 && (
          <Box mt={4} display="flex" flexDirection="column" style={{ gap: 16 }}>
            <TextField
              label={t("step1.companyName")}
              value={form.companyName}
              onChange={(e) => update("companyName", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label={t("step1.kana")}
              placeholder={t("step1.kanaPlaceholder")}
              value={form.kana}
              onChange={(e) => update("kana", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label={t("step1.representativeName")}
              value={form.representativeName}
              onChange={(e) => update("representativeName", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label={t("step1.email")}
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label={t("step1.phone")}
              placeholder={t("step1.phonePlaceholder")}
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label={t("step1.password")}
              type="password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <TextField
              label={t("step1.passwordConfirm")}
              type="password"
              value={form.passwordConfirm}
              onChange={(e) => update("passwordConfirm", e.target.value)}
              variant="outlined"
              fullWidth
              required
            />
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={goStep2}
              >
                {t("next")}
              </Button>
            </Box>
          </Box>
        )}

        {step === 2 && (
          <Box mt={4} display="flex" flexDirection="column" style={{ gap: 20 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t("step2.businessType")}</FormLabel>
              <RadioGroup
                row
                value={form.businessType}
                onChange={(e) =>
                  update("businessType", e.target.value as "delivery" | "visit")
                }
              >
                <FormControlLabel
                  value="delivery"
                  control={<Radio />}
                  label={t("step2.delivery")}
                />
                <FormControlLabel
                  value="visit"
                  control={<Radio />}
                  label={t("step2.visit")}
                />
              </RadioGroup>
            </FormControl>

            <Box display="flex" style={{ gap: 16 }}>
              <TextField
                label={t("step2.openingTime")}
                type="time"
                value={form.openingTime}
                onChange={(e) => update("openingTime", e.target.value)}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
              />
              <TextField
                label={t("step2.closingTime")}
                type="time"
                value={form.closingTime}
                onChange={(e) => update("closingTime", e.target.value)}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                helperText={t("step2.closingTimeHelp")}
              />
            </Box>

            <FormControl component="fieldset">
              <FormLabel component="legend">{t("step2.pointOperation")}</FormLabel>
              <RadioGroup
                row
                value={form.pointShopFlag ? "shop" : "group"}
                onChange={(e) => update("pointShopFlag", e.target.value === "shop")}
              >
                <FormControlLabel
                  value="group"
                  control={<Radio />}
                  label={t("step2.pointSharedAllShops")}
                />
                <FormControlLabel
                  value="shop"
                  control={<Radio />}
                  label={t("step2.perShop")}
                />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset">
              <FormLabel component="legend">{t("step2.guestRankOperation")}</FormLabel>
              <RadioGroup
                row
                value={form.guestCategoryShopFlag ? "shop" : "group"}
                onChange={(e) =>
                  update("guestCategoryShopFlag", e.target.value === "shop")
                }
              >
                <FormControlLabel
                  value="group"
                  control={<Radio />}
                  label={t("step2.guestRankShared")}
                />
                <FormControlLabel
                  value="shop"
                  control={<Radio />}
                  label={t("step2.perShop")}
                />
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset">
              <FormLabel component="legend">
                {t("step2.examTitle")}
              </FormLabel>
              <RadioGroup
                row
                value={form.examEnabled ? "yes" : "no"}
                onChange={(e) => update("examEnabled", e.target.value === "yes")}
              >
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label={t("step2.notUse")}
                />
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label={t("step2.use")}
                />
              </RadioGroup>
            </FormControl>

            {form.examEnabled && (
              <Box display="flex" style={{ gap: 12 }} alignItems="center">
                <FormControl variant="outlined" style={{ flex: 1 }}>
                  <InputLabel>{t("step2.judgeMethod")}</InputLabel>
                  <Select
                    value={form.examType}
                    label={t("step2.judgeMethod")}
                    onChange={(e) =>
                      update(
                        "examType",
                        e.target.value as "orderCount" | "shiftCount"
                      )
                    }
                  >
                    <MenuItem value="orderCount">{t("step2.orderCount")}</MenuItem>
                    <MenuItem value="shiftCount">{t("step2.shiftCount")}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={t("step2.examCount")}
                  type="number"
                  value={form.examCount}
                  onChange={(e) =>
                    update("examCount", parseInt(e.target.value) || 0)
                  }
                  variant="outlined"
                  style={{ width: 140 }}
                />
              </Box>
            )}

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button onClick={() => setStep(1)}>{t("back")}</Button>
              <Button variant="contained" color="primary" onClick={goStep3}>
                {t("next")}
              </Button>
            </Box>
          </Box>
        )}

        {step === 3 && (
          <Box mt={4} display="flex" flexDirection="column" style={{ gap: 16 }}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>{t("step3.referralLabel")}</InputLabel>
              <Select
                value={form.referralSource}
                label={t("step3.referralLabel")}
                onChange={(e) =>
                  update("referralSource", e.target.value as string)
                }
              >
                {REFERRAL_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              p={2}
              bgcolor="#f9fafb"
              border="1px solid #e5e7eb"
              borderRadius={8}
            >
              <Typography variant="subtitle2" gutterBottom>
                {t("step3.smsAuth")}
              </Typography>
              {!smsSent ? (
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t("step3.smsWillSend", { phone: form.phone })}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={sendSmsCode}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                  >
                    {loading ? t("step3.sending") : t("step3.sendSms")}
                  </Button>
                </Box>
              ) : verifyToken ? (
                <Alert severity="success">{t("step3.smsSuccess")}</Alert>
              ) : (
                <Box display="flex" style={{ gap: 8 }} alignItems="center">
                  <TextField
                    label={t("step3.codeLabel")}
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value)}
                    variant="outlined"
                    size="small"
                    inputProps={{ maxLength: 6 }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={verifyCode}
                  >
                    {loading ? t("step3.verifying") : t("step3.verify")}
                  </Button>
                </Box>
              )}
            </Box>

            <Box display="flex" alignItems="flex-start">
              <Checkbox
                checked={form.agreedTerms}
                onChange={(e) => update("agreedTerms", e.target.checked)}
                color="primary"
              />
              <Typography variant="body2">
                <a
                  href="https://tasteck.tech/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("step3.termsAgree1")}
                </a>{" "}
                {t("step3.termsAgreeMid")}{" "}
                <a
                  href="https://tasteck.tech/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("step3.termsAgree2")}
                </a>
                {t("step3.termsAgreeSuffix")}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button onClick={() => setStep(2)}>{t("back")}</Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={loading || !form.agreedTerms || !verifyToken}
                onClick={submit}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                {loading ? t("step3.submitting") : t("step3.submit")}
              </Button>
            </Box>
          </Box>
        )}

        {step === "done" && (
          <Box mt={4}>
            <Alert severity="success">
              <AlertTitle>{t("done.title")}</AlertTitle>
              {t("done.body")}
            </Alert>
            <Box mt={4} textAlign="center">
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => history.push("/login")}
              >
                {t("done.loginCta")}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Signup;
